export enum UserRole {
  STUDENT = 'student',
  TEACHER = 'teacher',
  ADMIN = 'admin',
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  avatar?: string;
  disabled?: boolean;
  created_at?: string;
  usn?: string;      // University Serial Number (unique)
  program?: string;  // Academic program/department
  cohort?: string;   // Cohort/batch identifier
}

export interface RubricLevel {
  id: string;
  name: string;
  percentage: number;
}

export interface RubricCriterion {
  id: string;
  title: string;
  description: string;
  weight: number;
}

export interface RubricDescriptor {
  criterionId: string;
  levelId: string;
  description: string;
}

export interface RubricGrid {
  levels: RubricLevel[];
  criteria: RubricCriterion[];
  descriptors: RubricDescriptor[];
}

export interface Journal {
  id: string;
  title: string;
  description: string;
  created_by: string;
  enrolled_count?: number;
  created_at: string;
}

export interface Entry {
  id: string;
  journal_id: string;
  title: string;
  instructions: string;
  rubric: RubricGrid;
  due_date?: string;
  is_visible: boolean;
  max_attempts: number;
  min_words: number;
  max_words: number;
  max_score: number;
  allow_resubmission: boolean;
  created_at: string;
}

export enum SubmissionStatus {
  SUBMITTED = 'submitted',
  PROCESSING = 'processing',
  FEEDBACK_READY = 'feedback_ready',
  GRADED = 'graded',
  PENDING_REVIEW = 'pending_review',
  ERROR = 'error'
}

export interface CriterionFeedback {
  criterion_id: string;
  criterion_name: string;
  level_achieved: string;
  percentage: number;
  specific_feedback: string;
  evidence: string;
}

export interface AIFeedback {
  error?: boolean;
  error_message?: string;
  overall_feedback: string;
  criterion_feedback: CriterionFeedback[];
  strengths: string[];
  growth_opportunities: string[];
  improvement_from_previous?: string | null;
  percentage_score?: number | null;
  total_score: number | null;
  max_score?: number;
  grade_letter: string | null;
  detailed_breakdown?: Record<string, number> | null;
  llm_confidence?: number;
  edited_by_teacher?: boolean;
  edited_at?: string;
  pending_manual_review?: boolean;
}

export interface Feedback {
  id: string;
  submission_id: string;
  ai_feedback?: AIFeedback;
  teacher_grade?: number | null;
  teacher_comments?: string;
  show_score_to_student: boolean;
  graded_at?: string;
  created_at: string;
}

export interface Submission {
  id: string;
  entry_id: string;
  student_id: string;
  student_name: string;
  student_usn?: string;     // Student's USN
  student_program?: string; // Student's program
  content_html: string;
  word_count: number;
  attempt_number: number;
  status: SubmissionStatus;
  submitted_at: string;
  feedback?: Feedback;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: User;
}

export interface JournalStats {
  journal: Journal;
  entries_count: number;
  enrolled_students: number;
  total_submissions: number;
  graded_submissions: number;
  ai_feedback_rate?: number;
}

export interface SystemStats {
  users: { total: number; students: number; teachers: number; admins: number; };
  journals: number;
  entries: number;
  submissions: { total: number; graded: number; };
}

export const DEFAULT_RUBRIC: RubricGrid = {
  levels: [
    { id: 'l1', name: 'Excellent', percentage: 100 },
    { id: 'l2', name: 'Good', percentage: 75 },
    { id: 'l3', name: 'Satisfactory', percentage: 50 },
    { id: 'l4', name: 'Needs Improvement', percentage: 25 },
  ],
  criteria: [
    { id: 'c1', title: 'Critical Thinking', description: 'Depth of analysis', weight: 30 },
    { id: 'c2', title: 'Self-Awareness', description: 'Personal learning', weight: 25 },
    { id: 'c3', title: 'Evidence & Examples', description: 'Specific examples', weight: 25 },
    { id: 'c4', title: 'Writing Quality', description: 'Clarity', weight: 20 },
  ],
  descriptors: [],
};
